import { createFileRoute, Link } from '@tanstack/react-router';
import { format } from 'date-fns';
import {
    ChevronLeft,
    ExternalLink,
    Loader2,
    MessageSquare,
    Sparkles,
    X,
} from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { CopyShareLinkButton } from '@apps/web/components/admin/copy_share_link_button';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Markdown } from '@apps/web/components/ui/markdown';
import { MultiSelect } from '@apps/web/components/ui/multi_select';
import { toast } from '@apps/web/lib/app_notifications';
import {
    analysisCallTypeLabel,
    sourceLabel,
} from '@apps/web/routes/_app/admin/calls/search_params';
import {
    analyzeAdminCall,
    setAdminCallTags,
    useAdminCall,
    useAdminCallTagVocabulary,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type CallTagRead } from '@libraries/typescript/api_client/schema_types';

// Sentinel option value for creating a tag from the combobox search term.
const CREATE_TAG_VALUE = '__create-tag__';

export const Route = createFileRoute('/_app/admin/calls/$callId')({
    component: CallDetailPage,
    head: () => ({ meta: [{ title: 'Call Detail' }] }),
});

function CallDetailPage() {
    const { callId } = Route.useParams();
    const { data: call, isLoading, error, mutate } = useAdminCall(callId);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
        );
    }

    // A 404 means the call genuinely does not exist; fall through to the
    // "not found" state below. Any other error is a real load failure.
    if (error && error.status !== 404) {
        return (
            <div className="flex flex-col gap-4 p-6">
                <BackLink />
                <div className="text-destructive py-12 text-center text-sm">
                    Failed to load this call. Please try again.
                </div>
            </div>
        );
    }

    if (!call) {
        return (
            <div className="flex flex-col gap-4 p-6">
                <BackLink />
                <div className="text-muted-foreground py-12 text-center text-sm">
                    Call not found.
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4 p-6">
            <BackLink />
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex flex-col gap-1">
                    <h1 className="text-lg font-semibold">{call.title}</h1>
                    <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-sm">
                        <Badge variant="secondary">
                            {sourceLabel(call.source_kind)}
                        </Badge>
                        {call.analysis_call_type ? (
                            <Badge variant="outline">
                                {analysisCallTypeLabel(call.analysis_call_type)}
                            </Badge>
                        ) : null}
                        <span>
                            {formatRange(call.started_at, call.ended_at)}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {!call.analysis_markdown?.trim() &&
                    !call.analysis_canonical_recording_id &&
                    call.transcript_text.trim() ? (
                        <AnalyzeCallButton
                            callId={call.id}
                            inProgress={call.analysis_in_progress}
                            refresh={() => mutate()}
                        />
                    ) : null}
                    {call.share_url ? (
                        <CopyShareLinkButton shareUrl={call.share_url} />
                    ) : null}
                    {call.recording_url ? (
                        <Button asChild={true} variant="outline" size="sm">
                            <a
                                href={call.recording_url}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <ExternalLink className="h-4 w-4" />
                                Open recording
                            </a>
                        </Button>
                    ) : null}
                </div>
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="flex flex-col gap-4 lg:col-span-2">
                    {!call.analysis_markdown?.trim() &&
                    call.analysis_canonical_recording_id ? (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">
                                    Analysis
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-muted-foreground text-sm">
                                    This recording is a duplicate note of an
                                    event that was analyzed on another
                                    recording.{' '}
                                    <Link
                                        to="/admin/calls/$callId"
                                        params={{
                                            callId: call.analysis_canonical_recording_id,
                                        }}
                                        className="text-foreground underline underline-offset-2"
                                    >
                                        View the analyzed recording
                                    </Link>
                                </p>
                            </CardContent>
                        </Card>
                    ) : null}
                    {call.analysis_markdown?.trim() ? (
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between gap-2">
                                    <CardTitle className="text-base">
                                        Analysis
                                    </CardTitle>
                                    {call.analysis_agent_run_id ? (
                                        <Button
                                            asChild={true}
                                            variant="outline"
                                            size="sm"
                                        >
                                            <Link
                                                to="/admin/agent/$runId"
                                                params={{
                                                    runId: call.analysis_agent_run_id,
                                                }}
                                            >
                                                <MessageSquare className="h-4 w-4" />
                                                Chat about this call
                                            </Link>
                                        </Button>
                                    ) : null}
                                </div>
                            </CardHeader>
                            <CardContent>
                                <Markdown>{call.analysis_markdown}</Markdown>
                            </CardContent>
                        </Card>
                    ) : null}
                    {call.summary.trim() ? (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">
                                    Summary
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="text-sm whitespace-pre-wrap">
                                {call.summary}
                            </CardContent>
                        </Card>
                    ) : null}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">
                                Transcript
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm whitespace-pre-wrap">
                            {call.transcript_text.trim()
                                ? call.transcript_text
                                : 'No transcript available.'}
                        </CardContent>
                    </Card>
                </div>
                <div className="flex flex-col gap-4">
                    <CallTagsPanel
                        callId={call.id}
                        tags={call.tags ?? []}
                        onRefresh={() => mutate()}
                    />
                    <Card className="h-fit">
                        <CardHeader>
                            <CardTitle className="text-base">
                                Participants
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="flex flex-col gap-3 text-sm">
                            <ParticipantList
                                names={call.participants}
                                emails={call.participant_emails}
                            />
                            {call.participant_domains.length > 0 ? (
                                <DetailRow label="Domains">
                                    {call.participant_domains.join(', ')}
                                </DetailRow>
                            ) : null}
                            <DetailRow label="Call type">
                                {call.call_type}
                            </DetailRow>
                            {call.source_meeting_id ? (
                                <DetailRow label="Source meeting ID">
                                    {call.source_meeting_id}
                                </DetailRow>
                            ) : null}
                            {call.source_recording_id ? (
                                <DetailRow label="Source recording ID">
                                    {call.source_recording_id}
                                </DetailRow>
                            ) : null}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}

// Eagerly queue an analysis instead of waiting for the 15-minute tick. The
// endpoint is idempotent, so a double click (or a race with the tick) is a
// safe no-op. Polls until the analysis lands; the button unmounts once
// `analysis_markdown` appears.
function AnalyzeCallButton({
    callId,
    inProgress,
    refresh,
}: {
    readonly callId: string;
    // Server-side in-flight state (the dispatch marker), so a queued
    // analysis keeps showing as such across page reloads.
    readonly inProgress: boolean;
    readonly refresh: () => void;
}) {
    const [queuedHere, setQueuedHere] = useState(false);
    const queued = queuedHere || inProgress;

    useEffect(() => {
        if (!queued) {
            return;
        }
        const interval = setInterval(refresh, 10_000);
        return () => clearInterval(interval);
    }, [queued, refresh]);

    const handleClick = async () => {
        setQueuedHere(true);
        try {
            await analyzeAdminCall(callId);
            toast.success('Analysis queued — usually takes a minute or two.', {
                persistNotification: false,
            });
        } catch {
            setQueuedHere(false);
            toast.error('Failed to queue the analysis.', {
                persistNotification: false,
            });
        }
    };

    return (
        <Button
            variant="outline"
            size="sm"
            onClick={handleClick}
            disabled={queued}
        >
            {queued ? (
                <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
                <Sparkles className="h-4 w-4" />
            )}
            {queued ? 'Analyzing…' : 'Analyze call'}
        </Button>
    );
}

function BackLink() {
    return (
        <Link
            to="/admin/calls"
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
        >
            <ChevronLeft className="h-4 w-4" />
            Back to calls
        </Link>
    );
}

function ParticipantList({
    names,
    emails,
}: {
    readonly names: Array<string>;
    readonly emails: Array<string>;
}) {
    if (names.length === 0 && emails.length === 0) {
        return <span className="text-muted-foreground">No participants.</span>;
    }
    return (
        <div className="flex flex-col gap-1">
            {names.map((name) => (
                <span key={name}>{name}</span>
            ))}
            {emails.map((email) => (
                <span key={email} className="text-muted-foreground text-xs">
                    {email}
                </span>
            ))}
        </div>
    );
}

function DetailRow({
    label,
    children,
}: {
    readonly label: string;
    readonly children: ReactNode;
}) {
    return (
        <div className="flex flex-col">
            <span className="text-muted-foreground text-xs">{label}</span>
            <span className="break-words">{children}</span>
        </div>
    );
}

function CallTagsPanel({
    callId,
    tags,
    onRefresh,
}: {
    readonly callId: string;
    readonly tags: ReadonlyArray<CallTagRead>;
    readonly onRefresh: () => Promise<unknown>;
}) {
    const { data: vocabulary } = useAdminCallTagVocabulary();
    const [search, setSearch] = useState('');
    const [pendingSlugs, setPendingSlugs] = useState<ReadonlySet<string>>(
        new Set(),
    );
    // Desired labels, updated optimistically per toggle so consecutive
    // selections build on each other instead of the last server snapshot.
    // The ref is the always-current base for delta updates: two clicks in
    // one render batch both see the same stale props, so each change is
    // applied as a diff against the ref, not taken as the full next array.
    // Saves are serialized through a promise chain — overlapping full-set
    // PUTs could otherwise land out of order and drop the newer selection.
    const [selectedLabels, setSelectedLabels] = useState<Array<string>>(() =>
        tags.map((tag) => tag.label),
    );
    const selectedLabelsRef = useRef(selectedLabels);
    const saveChain = useRef<Promise<void>>(Promise.resolve());
    const pendingSaves = useRef(0);
    useEffect(() => {
        // Refreshes fired by earlier saves in the chain must not clobber
        // optimistic selections that later queued saves have not persisted
        // yet; the final save's refresh (pending count back at zero) is the
        // one that re-syncs from the server.
        if (pendingSaves.current > 0) {
            return;
        }
        selectedLabelsRef.current = tags.map((tag) => tag.label);
        setSelectedLabels(selectedLabelsRef.current);
    }, [tags]);

    // The full vocabulary (most-used first) plus this call's own tags, so
    // people pick established names instead of typing near-duplicates.
    const labelBySlug = useMemo(() => {
        const map = new Map<string, string>();
        for (const tag of vocabulary ?? []) {
            map.set(tag.slug, tag.label);
        }
        for (const tag of tags) {
            map.set(tag.slug, tag.label);
        }
        return map;
    }, [vocabulary, tags]);

    const searchTerm = search.trim();
    const options = useMemo(() => {
        const base = [...labelBySlug.entries()].map(([value, label]) => ({
            value,
            label,
        }));
        const alreadyExists = base.some(
            (option) => option.label.toLowerCase() === searchTerm.toLowerCase(),
        );
        if (searchTerm && !alreadyExists) {
            base.push({
                value: CREATE_TAG_VALUE,
                label: `Create “${searchTerm}”`,
            });
        }
        return base;
    }, [labelBySlug, searchTerm]);

    const slugByLabel = useMemo(() => {
        const map = new Map<string, string>();
        for (const [slug, label] of labelBySlug.entries()) {
            map.set(label, slug);
        }
        return map;
    }, [labelBySlug]);
    const selectedSlugs = useMemo(
        () =>
            selectedLabels.map(
                (label) => slugByLabel.get(label) ?? `label:${label}`,
            ),
        [selectedLabels, slugByLabel],
    );

    const saveLabels = (labels: Array<string>) => {
        selectedLabelsRef.current = labels;
        setSelectedLabels(labels);
        pendingSaves.current += 1;
        saveChain.current = saveChain.current.then(async () => {
            try {
                await setAdminCallTags(callId, {
                    tags: labels.map((label) => ({ label })),
                });
            } catch {
                toast.error('Failed to update tags', {
                    persistNotification: false,
                });
            } finally {
                // Decrement before refreshing so the refresh-triggered tag
                // sync knows whether this was the chain's last save.
                pendingSaves.current -= 1;
            }
            await onRefresh();
        });
    };

    const slugToLabel = (slug: string): string => {
        if (slug === CREATE_TAG_VALUE) {
            return searchTerm;
        }
        if (slug.startsWith('label:')) {
            return slug.slice('label:'.length);
        }
        return labelBySlug.get(slug) ?? slug;
    };

    const applySelection = (nextSlugs: Array<string>) => {
        // `nextSlugs` was computed from this render's (possibly stale)
        // `selected` prop, so only its diff against that same render's
        // selection is trustworthy; apply that diff to the current ref.
        const before = new Set(selectedSlugs);
        const after = new Set(nextSlugs);
        const addedLabels = nextSlugs
            .filter((slug) => !before.has(slug))
            .map(slugToLabel)
            .filter((label) => label !== '');
        const removedLabels = new Set(
            selectedSlugs.filter((slug) => !after.has(slug)).map(slugToLabel),
        );
        const next = [
            ...selectedLabelsRef.current.filter(
                (label) => !removedLabels.has(label),
            ),
            ...addedLabels.filter(
                (label) => !selectedLabelsRef.current.includes(label),
            ),
        ];
        setSearch('');
        saveLabels(next);
    };

    const handleRemove = (tag: CallTagRead) => {
        setPendingSlugs((current) => new Set(current).add(tag.slug));
        saveLabels(selectedLabels.filter((label) => label !== tag.label));
        void saveChain.current.finally(() => {
            setPendingSlugs((current) => {
                const next = new Set(current);
                next.delete(tag.slug);
                return next;
            });
        });
    };

    return (
        <Card className="h-fit">
            <CardHeader>
                <CardTitle className="text-base">Tags</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
                <MultiSelect
                    options={options}
                    selected={selectedSlugs}
                    onChange={applySelection}
                    compactLabel="Add tags…"
                    matchTriggerWidth={true}
                    searchValue={search}
                    onSearchChange={setSearch}
                />
                {tags.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                        No tags yet.
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-2">
                        {tags.map((tag) => (
                            <Badge
                                key={tag.slug}
                                variant="secondary"
                                className="min-w-0 gap-1.5"
                            >
                                <span
                                    className="size-2 rounded-full"
                                    style={{
                                        backgroundColor:
                                            tag.color ?? 'currentColor',
                                    }}
                                />
                                <span className="truncate">{tag.label}</span>
                                <button
                                    type="button"
                                    aria-label={`Remove ${tag.label}`}
                                    disabled={pendingSlugs.has(tag.slug)}
                                    className="ml-0.5 shrink-0"
                                    onClick={() => {
                                        handleRemove(tag);
                                    }}
                                >
                                    {pendingSlugs.has(tag.slug) ? (
                                        <Loader2 className="size-3 animate-spin" />
                                    ) : (
                                        <X className="size-3" />
                                    )}
                                </button>
                            </Badge>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
function formatRange(startedAt: string | null, endedAt: string | null): string {
    if (!startedAt) {
        return 'Date unknown';
    }
    const start = new Date(startedAt);
    if (Number.isNaN(start.getTime())) {
        return 'Date unknown';
    }
    const startLabel = format(start, 'PPp');
    if (!endedAt) {
        return startLabel;
    }
    const end = new Date(endedAt);
    if (Number.isNaN(end.getTime())) {
        return startLabel;
    }
    return `${startLabel} – ${format(end, 'p')}`;
}
